import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { StateService } from "./services/state.service";
import { TitleService } from "./services/title.service";
import { ChatbotMsalService } from "./main-container/chat-bot/chatbot-msal.service";
import { environment } from "../environments/environment";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = "phoBoy-ui";
  readonly environment = environment;
  result: any;
  toastMarginClass: "right-toolbar-open" | "right-toolbar-close" =
    "right-toolbar-open";

  constructor(
    public stateService: StateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public messageService: MessageService,
    private readonly titleService: TitleService,
    private readonly chatbotMsalService: ChatbotMsalService,
  ) {
    // Process MSAL redirect FIRST — before any routing strips the #code= hash
    if (environment.maintenanceMode) {
      return;
    }

    this.processMsalRedirect();

    const mac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
    if (!mac) {
      window.document.body.id = "custom-scrollbar";
    }

    this.route.queryParams.subscribe((params: any) => {
      if (params.access_token && params.refresh_token) {
        window.localStorage.c = params.access_token;
        window.localStorage.d = params.refresh_token;
        window.localStorage.i = params.id;
        // This is the first time here we set the tokenExpiryDate
        this.stateService.setTokenExpiryDate();

        const redirectURL =
          sessionStorage.getItem("callbackURL") || this.router.url;
        sessionStorage.removeItem("callbackURL");

        if (redirectURL) {
          const urlTree = this.router.parseUrl(redirectURL);

          // Extract the path safely
          const path =
            urlTree.root.children["primary"]?.segments
              .map((segment) => segment.path)
              .join("/") || "";

          // Extract query parameters safely
          const queryParams = Object.keys(urlTree.queryParams).length
            ? urlTree.queryParams
            : null;

          // Navigate with or without query params
          this.router.navigate([path], {
            ...(queryParams ? { queryParams } : {}), // Add query params only if they exist
            replaceUrl: true,
          });
        }
      }
    });
  }

  ngOnInit() {
    if (environment.maintenanceMode) {
      return;
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.titleService.setDefaultTitle();
        this.titleService.setFavicon();
      }
    });
    this.openToast();
    this.openSoftWarningToast();
  }

  closeDownloadToast() {
    this.stateService.isDownload = false;
    this.messageService.clear("downloadToast");
  }
  openToast() {
    this.stateService.downloadToastEvents$.subscribe(() => {
      this.messageService.add({
        key: "downloadToast",
        severity: "info",
        summary: "Warn Message",
        sticky: true,
        closable: false,
      });
    });
  }
  openSoftWarningToast() {
    this.stateService.softWarningToastEvents$.subscribe((data) => {
      if (data) {
        this.toastMarginClass = data["marginClass"];
        this.messageService.add({
          key: "softWarningToast",
          severity: "info",
          summary: "Warn Message",
          detail: data["message"],
          life: 5000,
          closable: false,
        });
      }
    });
  }
  closeSoftWarningToast() {
    this.messageService.clear("softWarningToast");
  }

  private async processMsalRedirect(): Promise<void> {
    // Only process if returning from an MSAL redirect (code param in URL)
    const hasAuthCode =
      window.location.hash.includes("code=") ||
      window.location.search.includes("code=");
    if (!hasAuthCode) {
      return;
    }
    try {
      await this.chatbotMsalService.initialize();
      const result = await this.chatbotMsalService.handleRedirect();
      if (result) {
        const savedUrl = sessionStorage.getItem("chatbot_redirect_url");
        sessionStorage.removeItem("chatbot_redirect_url");
        if (savedUrl) {
          const parsed = new URL(savedUrl);
          this.router.navigateByUrl(parsed.pathname + parsed.search, {
            replaceUrl: true,
          });
        } else {
          this.router.navigate(["/studio/chatbot"], { replaceUrl: true });
        }
      }
    } catch {
      // MSAL redirect processing failed — non-MSAL pages are unaffected
    }
  }
}
